/**
 * ApplyWizard — 6-step Apply Now stepper shell.
 *
 * Rail pattern per rcp-apply-now-step3-cip-id.html. Rail + main column. Steps
 * announced with aria-current="step" on the active step (per mockup #3 fix).
 *
 * State is lifted here so steps can share the `draftId` / `cartId`.
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

export interface ApplyWizardState {
  draftId: string | null;
  cartId: string | null;
  productCode: string | null;
  fundingAmount: number | null;
  idConfidence: Record<string, number>;
  setDraft: (draftId: string, cartId: string) => void;
  setProductCode: (code: string) => void;
  setFundingAmount: (n: number) => void;
  setIdConfidence: (key: string, score: number) => void;
  gotoStep: (step: number) => void;
}

const ApplyCtx = createContext<ApplyWizardState | null>(null);

export function useApplyWizard(): ApplyWizardState {
  const ctx = useContext(ApplyCtx);
  if (!ctx) throw new Error('useApplyWizard must be used inside ApplyWizard');
  return ctx;
}

const STEPS: Array<{ path: string; label: string }> = [
  { path: 'start',       label: 'Get started' },
  { path: 'applicant',   label: 'About you' },
  { path: 'cip-id',      label: 'Verify ID' },
  { path: 'disclosures', label: 'Disclosures' },
  { path: 'funding',     label: 'Funding' },
  { path: 'success',     label: 'All done' },
];

export function ApplyWizard({ children }: { children?: ReactNode }): JSX.Element {
  const [draftId, setDraftId] = useState<string | null>(null);
  const [cartId, setCartId] = useState<string | null>(null);
  const [productCode, setProductCode] = useState<string | null>(null);
  const [fundingAmount, setFundingAmount] = useState<number | null>(null);
  const [idConfidence, setIdConfidenceMap] = useState<Record<string, number>>({});
  const navigate = useNavigate();
  const location = useLocation();

  const value = useMemo<ApplyWizardState>(
    () => ({
      draftId,
      cartId,
      productCode,
      fundingAmount,
      idConfidence,
      setDraft: (d: string, c: string) => {
        setDraftId(d);
        setCartId(c);
      },
      setProductCode: (code: string) => setProductCode(code),
      setFundingAmount: (n: number) => setFundingAmount(n),
      setIdConfidence: (key: string, score: number) =>
        setIdConfidenceMap((cur) => ({ ...cur, [key]: score })),
      gotoStep: (step: number) => {
        const target = STEPS[step];
        if (target) navigate(`/apply/${target.path}`);
      },
    }),
    [draftId, cartId, productCode, fundingAmount, idConfidence, navigate],
  );

  const currentStepIndex = STEPS.findIndex((s) => location.pathname.endsWith(`/${s.path}`));

  return (
    <ApplyCtx.Provider value={value}>
      <div className="wizard">
        <nav className="rail" aria-label="Application progress">
          <h4>Application</h4>
          <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {STEPS.map((step, idx) => {
              const isCurrent = idx === currentStepIndex;
              const isDone = idx < currentStepIndex;
              return (
                <li
                  key={step.path}
                  className="step"
                  aria-current={isCurrent ? 'step' : undefined}
                  data-done={isDone ? 'true' : undefined}
                >
                  <span className="step-num" aria-hidden="true">{isDone ? '✓' : idx + 1}</span>
                  <span className="step-lbl">
                    <span className="visually-hidden">
                      Step {idx + 1} of {STEPS.length}{isDone ? ', completed' : isCurrent ? ', current' : ''}:
                    </span>
                    {' '}{step.label}
                  </span>
                </li>
              );
            })}
          </ol>
        </nav>
        <div>{children ?? <Outlet />}</div>
      </div>
    </ApplyCtx.Provider>
  );
}
